// The player dialog's Games tab (F-CAT-04, spec/13 §8): what the player played at the venues the member works at, as
// the stations detected it — "Priya played Valorant for 50 minutes". The totals first (time played, titles, when
// last), then each title most played first with its sessions and when last played, then the newest plays with the
// PC they were on. Read-only: plays are recorded by the stations, never edited here.

import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { EmptyState, Skeleton, StatCard } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { useMyVenues } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { useScopeIds } from '../../shell/scope';
import { Section, SummaryStrip } from '../../ui';
import { TickingNumber } from '../../ui/ticking';
import { usePlayerGames } from './queries';

export function GamesTab({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const venues = useMyVenues();
  const games = usePlayerGames(scope.organizationId, playerId);
  const venueName = (venueId: string) => (venues.data ?? []).find((venue) => venue.id === venueId)?.name ?? venueId;
  const data = games.data;

  if (data === undefined) {
    return (
      <div className="console-people-tab">
        {games.isError ? (
          <LoadFailure error={games.error} message={t('players.history.unavailable')} onRetry={() => void games.refetch()} />
        ) : (
          <Skeleton shape="text" lines={6} />
        )}
      </div>
    );
  }

  if (data.titles.length === 0) {
    return (
      <div className="console-people-tab">
        <EmptyState size="compact" title={t('players.games.empty')} />
      </div>
    );
  }

  const last = data.titles.reduce((newest, title) => (title.lastPlayedAt > newest ? title.lastPlayedAt : newest), data.titles[0]?.lastPlayedAt ?? '');

  return (
    <div className="console-people-tab">
      <SummaryStrip label={t('players.drawer.tab.games')}>
        <StatCard label={t('players.games.total')} value={<TickingNumber className="console-num" value={data.totalSeconds} format={(seconds) => format.duration(seconds)} />} />
        <StatCard label={t('players.games.titles')} value={<TickingNumber className="console-num" value={data.titles.length} format={(count) => format.number(count)} />} />
        <StatCard label={t('players.games.lastPlayed')} value={<span className="console-num">{format.date(last, 'medium')}</span>} />
      </SummaryStrip>

      <Section title={t('players.games.mostPlayed')}>
        <ul className="console-people-lines">
          {data.titles.map((title) => (
            <li key={title.titleId} className="console-people-line">
              <span className="console-people-line__what">
                <span>{title.name}</span>
                <span className="console-subtle">
                  {t('players.games.titleLine', {
                    sessions: t('players.games.sessions', { count: title.sessions }),
                    date: format.date(title.lastPlayedAt, 'medium'),
                  })}
                </span>
              </span>
              <span className="console-num">{format.duration(title.seconds)}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t('players.games.recent')}>
        <ul className="console-people-lines">
          {data.recent.map((play) => (
            <li key={play.id} className="console-people-line">
              <span className="console-people-line__what">
                <span>{play.name}</span>
                <span className="console-subtle">
                  {format.dateTime(play.startedAt, { date: 'medium', time: 'short' })}
                  {' · '}
                  {play.stationNumber === null ? venueName(play.venueId) : t('players.games.playAt', { station: play.stationNumber, venue: venueName(play.venueId) })}
                </span>
              </span>
              <span className="console-num">{format.duration(play.seconds)}</span>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
